import {
  AppBar,
  Button,
  buttonClasses,
  Checkbox,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Menu,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { CloseCircle, DirectionDown, DocumentDownload, Magnifier } from 'asset';
import {
  DeleteDialog,
  FilterMenu,
  HeadTable,
  Layout,
  PopupCreateOrEditTopupPayout,
  TableCaption,
} from 'components';
import DeleteAllDialog from 'components/common/DeleteAllDialog';
import {
  ButtonDelete,
  ButtonDetail,
  ButtonEdit,
} from 'components/common/table';
import PopupDetailPayment from 'components/Popups/Transaction/PopupDetailPayment';
import { drawerWidth } from 'config';
import FileSaver from 'file-saver';
import { getSortItem, hasPermission, NotifyService, printPrice } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import {
  CASH_TRANSACTION_CODE,
  cashTXTStatusColor,
  FilterOption,
  FilterOptionType,
  FilterValue,
  GET_PAYMENT_PARAMS_REQUEST,
  PAYMENT_TYPE,
  TYPE_POPUP,
} from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import { deleteAllPaymentApi, getPaymentsExportCSVApi } from 'services';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setCashFeeListParams } from 'store/reducers/cash-fee';
import { getCashFeeListRequest } from 'store/reducers/cash-fee/actionTypes';
import { headCells } from './data';

const filterOptions: FilterOption[] = [
  {
    name: 'User',
    key: 'user',
    type: FilterOptionType.TEXT,
  },
  {
    name: 'TXN Date',
    key: 'timestamp',
    type: FilterOptionType.DATE_PICKER,
  },
];

const CashFee = () => {
  const theme = useTheme();
  const { moment, formatDateTime } = useDateTime();
  const dispatch = useAppDispatch();
  const { cashFee_list } = useAppSelector(
    (state) => state.cashFeeReducer.cashFee
  );
  const { params } = cashFee_list;
  const [anchorElAction, setAnchorElAction] = useState<null | HTMLElement>(
    null
  );
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openDetailPopup, setOpenDetailPopup] = useState(false);
  const [itemCashFee, setItemCashFee] = useState<PAYMENT_TYPE>();
  const [openDeleteAll, setOpenDeleteAll] = useState<boolean>(false);
  const [openCreateOrEdit, setOpenCreateOrEdit] = useState<boolean>(false);

  const onSetCashFeeListParams = (
    params: GET_PAYMENT_PARAMS_REQUEST | null
  ) => {
    dispatch(setCashFeeListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getCashFeeListRequest(params));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.keyword ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpenDelete = (event: any, data: PAYMENT_TYPE) => {
    event.stopPropagation();
    setItemCashFee(data);
    setOpenDeletePopup(true);
  };

  const setOpenDetail = (event: any, data: PAYMENT_TYPE) => {
    event.stopPropagation();
    setItemCashFee(data);
    setOpenDetailPopup(true);
  };

  const setOpenEdit = (event: any, data: PAYMENT_TYPE) => {
    event.stopPropagation();
    setItemCashFee(data);
    setOpenCreateOrEdit(true);
  };

  const setOpenCreate = () => {
    setOpenCreateOrEdit(true);
  };

  const handleOpenBulkAction = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAction(event.currentTarget);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = cashFee_list.records.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetCashFeeListParams({ ...params, page: 1, limit });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows =
  //   page > 0
  //     ? Math.max(0, (1 + page) * rowsPerPage - jobs.metadata.total_count)
  //     : 0;

  const { items } = usePagination({
    count: cashFee_list.metadata.total_pages,
    page: cashFee_list.metadata.page,
    onChange(_event, page) {
      onSetCashFeeListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.keyword = keyword;
    else delete _params.keyword;
    onSetCashFeeListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemCashFee(undefined);
        break;
      case TYPE_POPUP.DETAIL:
        setOpenDetailPopup(false);
        setItemCashFee(undefined);
        break;
      case TYPE_POPUP.CREATE_OR_EDIT:
        setOpenCreateOrEdit(false);
        setItemCashFee(undefined);
        break;
    }
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getCashFeeListRequest(params));
  };

  const deleteManyCashFee = async () => {
    const cashFeeIds: any = selected;
    await deleteAllPaymentApi(cashFeeIds)
      .then((r) => {
        onReloadList();
        setOpenDeleteAll(false);
        setSelected([]);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const filterValue: FilterValue = useMemo(() => {
    return {
      user: cashFee_list.params?.user,
      timestamp: cashFee_list.params?.timestamp,
    };
  }, [cashFee_list.params]);

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };
    delete _params.user;
    delete _params.timestamp;
    onSetCashFeeListParams({
      ..._params,
      ...value,
      page: 1,
    });
  };

  const sortItem = useMemo(() => {
    return getSortItem(cashFee_list.params?.sort);
  }, [cashFee_list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof PAYMENT_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetCashFeeListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const onExportCSV = async () => {
    await getPaymentsExportCSVApi({
      sort: params?.sort,
      code: params?.code,
    })
      .then((res) => {
        FileSaver.saveAs(
          res.data,
          `${moment().format('YYYY-MM-DD')} Cash_Fee.csv`
        );
      })
      .catch((e) => NotifyService.error(e));
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Finance / Cash Fee
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                flexGrow: 1,
                // paddingRight: '9px',
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              // autoComplete=""
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
            />
          </Grid>
          <Grid display="flex" gap="8px">
            {hasPermission(['superadmin']) && (
              <>
                <Button
                  sx={{
                    background: '#fff',
                    border: `1px solid #226FE3`,
                    borderRadius: '3px',
                    boxShadow: 'none',

                    color: '#226FE3',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    textTransform: 'none',

                    p: '6px 16px',
                    height: '30px',
                    alignItems: 'flex-end',
                    ':hover': {
                      background: 'rgba(34, 111, 227, 0.08)',
                      border: `1px solid ${theme.palette.secondary.main}`,
                      borderRadius: '3px',
                      boxShadow: 'none',
                    },
                    [`& .${buttonClasses.endIcon}`]: {
                      mb: '-3px',
                      ml: '3px',
                    },
                  }}
                  variant="contained"
                  endIcon={<DirectionDown />}
                  onClick={handleOpenBulkAction}
                >
                  Bulk Action
                </Button>
                <Menu
                  sx={{
                    mt: '45px',
                    '& .MuiList-padding': {
                      pt: '0px !important',
                      pb: '0px !important',
                    },
                  }}
                  id="menu-appbar"
                  anchorEl={anchorElAction}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElAction)}
                  onClose={() => setAnchorElAction(null)}
                >
                  <Stack
                    sx={{
                      width: '319px',
                      p: '14px 22px 25px 23px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      ':hover': {
                        backgroundColor: 'white.light',
                      },
                    }}
                  >
                    <Stack spacing="10px">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '100%',
                        }}
                      >
                        Bulk Actions
                      </Typography>
                      <Button
                        startIcon={
                          <CloseCircle
                            width={15}
                            heigh={15}
                            color={theme.palette.secondary.main}
                          />
                        }
                        variant="outlined"
                        color="secondary"
                        sx={{
                          p: 0,
                          fontWeight: 600,
                          fontSize: '15px',
                          lineHeight: 1,
                          width: '230px',
                          height: '32px',
                          borderRadius: '10px',
                          '& .MuiButton-startIcon': {
                            mr: '5px',
                          },
                        }}
                        onClick={() => setOpenDeleteAll(true)}
                      >
                        Delete Selected Cash Fee
                      </Button>
                    </Stack>
                  </Stack>
                </Menu>
                <Button
                  sx={{
                    width: '131px',
                    height: 30,
                    fontWeight: 600,
                    fontSize: '13px',
                    border: `1px solid ${theme.palette.secondary.main}`,
                    p: '0px',
                  }}
                  variant="outlined"
                  color="secondary"
                  endIcon={<DocumentDownload />}
                  onClick={onExportCSV}
                >
                  Export to CSV
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{
                    height: 30,
                    width: '72px',
                    fontWeight: 600,
                    fontSize: '13px',
                    lineHeight: '100%',
                  }}
                  onClick={setOpenCreate}
                >
                  Create
                </Button>
              </>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
            maxWidth: 'calc(100vw - 282px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<PAYMENT_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={cashFee_list.records.length}
              headCells={headCells}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {cashFee_list.records.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                      }}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={(e) => handleClick(e, row.id)}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row.timestamp)}
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.number}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.type}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.user?.first_name}&nbsp;{row.user?.last_name}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.doc_ref_no}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        color: row.total_amount > 0 ? '#0CAD35' : '#000',
                      }}
                    >
                      {printPrice(row.total_amount)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        color:
                          cashTXTStatusColor[row.status || ''] || '#B6B6B6',
                      }}
                    >
                      {row.status}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.walletTransaction?.number}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <ButtonDetail onClick={(e) => setOpenDetail(e, row)} />
                        {hasPermission(['superadmin']) && (
                          <>
                            <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                            <ButtonDelete
                              onClick={(e) => setOpenDelete(e, row)}
                            />
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={cashFee_list.metadata}
          items={items}
        />
      </Stack>

      {openDeletePopup && (
        <DeleteDialog
          name=""
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={itemCashFee?.id}
          onSuccess={onReloadList}
          type="cashFee"
        />
      )}
      {openDeleteAll && (
        <DeleteAllDialog
          open={openDeleteAll}
          onClose={() => setOpenDeleteAll(false)}
          onClickDelete={deleteManyCashFee}
        />
      )}
      {openDetailPopup && (
        <PopupDetailPayment
          open
          title="Cash Fee detail"
          subject={itemCashFee?.id}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.DETAIL)}
        />
      )}
      <PopupCreateOrEditTopupPayout
        open={openCreateOrEdit}
        title="Cash Fee"
        typeCode={CASH_TRANSACTION_CODE.CASH_FEE}
        subject={itemCashFee?.id}
        onSuccess={onReloadList}
        handleClosePopup={() => onClosePopup(TYPE_POPUP.CREATE_OR_EDIT)}
      />
    </Layout>
  );
};

export default CashFee;
